import { gsap, ScrollTrigger } from "@/composeables/gsap";

// Marquee-Geschwindigkeit (visuell konstant, unabhängig von der Logo-Anzahl).
const SPEED_PX_PER_SEC = 60;

export const init = () => {

    gsap.utils.toArray(".section-logo-wall").forEach(function (section) {

        const viewport = section.querySelector(".section-logo-wall__logos");
        const track = section.querySelector(".section-logo-wall__track");
        if (!viewport || !track) return;

        const getLogos = () =>
            [...track.querySelectorAll(".section-logo-wall__logo:not([data-clone])")];
        if (!getLogos().length) return;

        let loopTween = null;
        let revealBatch = null;
        let io = null;

        const reset = () => {
            if (loopTween) { loopTween.kill(); loopTween = null; }
            if (revealBatch) { revealBatch.forEach((st) => st.kill && st.kill()); revealBatch = null; }
            if (io) { io.disconnect(); io = null; }
            track.querySelectorAll("[data-clone]").forEach((c) => c.remove());
            section.classList.remove("section-logo-wall--marquee");
            gsap.set(track, { clearProps: "transform" });
            gsap.set(getLogos(), { clearProps: "opacity,visibility,transform" });
        };

        // Gap zwischen den Logos aus dem berechneten Style lesen (Fallback 0).
        const getGap = () => {
            const g = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap);
            return Number.isFinite(g) ? g : 0;
        };

        const build = () => {
            reset();

            const originals = getLogos();
            const gap = getGap();

            // Echte einzeilige Inhaltsbreite aus den Logo-Breiten + Gaps berechnen.
            // Nicht über scrollWidth messen: der Track ist ein Grid-Item mit fester
            // Spaltenbreite und staucht seine Kinder, statt überzulaufen -> scrollWidth
            // bliebe = Containerbreite und der Overflow würde nie erkannt.
            const contentWidth =
                originals.reduce((sum, logo) => sum + logo.getBoundingClientRect().width, 0) +
                gap * Math.max(0, originals.length - 1);

            const overflows = contentWidth > viewport.clientWidth + 1;

            if (!overflows) {
                // Passt in den Viewport (z. B. Desktop, 6 Logos): statisch + Reveal wie bisher.
                gsap.set(originals, { autoAlpha: 0, y: 24 });
                revealBatch = ScrollTrigger.batch(originals, {
                    interval: 0.1,
                    invalidateOnRefresh: true,
                    start: "top bottom",
                    end: "bottom top",
                    onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, stagger: 0.12, overwrite: true, ease: "expo", duration: 0.9 }),
                });
                return;
            }

            // Passt nicht: Marquee. Set einmal klonen -> nahtloser Endlos-Loop.
            // (Die --marquee-Klasse setzt den Track auf nowrap + width:max-content,
            //  sodass Originale + Klone in einer Zeile nebeneinander liegen.)
            section.classList.add("section-logo-wall--marquee");
            gsap.set(originals, { autoAlpha: 1 });
            originals.forEach((logo) => {
                const clone = logo.cloneNode(true);
                clone.setAttribute("data-clone", "");
                clone.setAttribute("aria-hidden", "true");
                gsap.set(clone, { autoAlpha: 1 });
                track.appendChild(clone);
            });

            // Ein Durchlauf = Breite eines kompletten Original-Sets inkl. des Gaps
            // zum ersten Klon, damit die Wiederholung exakt nahtlos anschließt.
            const oneSetWidth = contentWidth + gap;
            if (oneSetWidth <= 0) return;

            loopTween = gsap.to(track, {
                x: -oneSetWidth,
                duration: oneSetWidth / SPEED_PX_PER_SEC,
                ease: "none",
                repeat: -1,
            });

            // Off-screen pausieren (Performance bei der Endlos-Animation).
            io = new IntersectionObserver(([entry]) => {
                if (!loopTween) return;
                entry.isIntersecting ? loopTween.play() : loopTween.pause();
            }, { rootMargin: "150px" });
            io.observe(section);
        };

        build();

        // Bei Resize + nach dem Laden der Logo-Bilder (Breiten ändern sich) neu bewerten.
        let resizeTimer = null;
        const scheduleBuild = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(build, 200); };
        window.addEventListener("resize", scheduleBuild);
        window.addEventListener("load", scheduleBuild);

    });
}

export default init;
